import React, { useEffect, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, Label, Input, FormFeedback } from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';

const EventRegistrationModal = ({ isOpen, toggle, eventId, prefill, onSubmit, onCancel, isSubmitting }) => {
  const validationSchema = useMemo(() => Yup.object({
    full_name: Yup.string().trim().optional(),
    email: Yup.string().email('Invalid email').optional(),
    phone_number: Yup.string().optional(),
    guests_count: Yup.number()
      .typeError('Guests must be a number')
      .min(0, 'Guests must be >= 0')
      .optional(),
    attendees_count: Yup.number()
      .typeError('Attendees must be a number')
      .min(1, 'Total attendees must be >= 1')
      .optional(),
    notes: Yup.string().optional(),
  }), []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      full_name: prefill?.full_name || '',
      email: prefill?.email || '',
      phone_number: prefill?.phone_number || '',
      guests_count: prefill?.guests_count ?? '',
      attendees_count: prefill?.attendees_count ?? '',
      notes: prefill?.notes || '',
    },
    validationSchema,
    onSubmit: (values) => {
      const payload = { ...values };
      // Prefer attendees_count if both provided, else use the one provided
      if (payload.guests_count === '') delete payload.guests_count;
      if (payload.attendees_count === '') delete payload.attendees_count;
      if (!payload.full_name) delete payload.full_name;
      if (!payload.email) delete payload.email;
      if (!payload.phone_number) delete payload.phone_number;
      if (!payload.notes) delete payload.notes;
      onSubmit(payload);
    },
  });

  // Derive attendees from guests for helper text
  const totalAttendees = useMemo(() => {
    const guests = Number(formik.values.guests_count || 0);
    return 1 + (Number.isNaN(guests) ? 0 : guests);
  }, [formik.values.guests_count]);

  useEffect(() => {
    if (!isOpen) formik.resetForm();
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>Event Registration</ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <div className="mb-3">
            <Label>Full name</Label>
            <Input name="full_name" value={formik.values.full_name} onChange={formik.handleChange} />
          </div>
          <div className="mb-3">
            <Label>Email</Label>
            <Input name="email" type="email" value={formik.values.email} onChange={formik.handleChange} invalid={!!(formik.touched.email && formik.errors.email)} />
            {formik.touched.email && formik.errors.email && <FormFeedback>{formik.errors.email}</FormFeedback>}
          </div>
          <div className="mb-3">
            <Label>Phone</Label>
            <Input name="phone_number" value={formik.values.phone_number} onChange={formik.handleChange} />
          </div>
          <div className="mb-3">
            <Label>Guests</Label>
            <Input name="guests_count" value={formik.values.guests_count} onChange={formik.handleChange} invalid={!!(formik.touched.guests_count && formik.errors.guests_count)} />
            {formik.touched.guests_count && formik.errors.guests_count && <FormFeedback>{formik.errors.guests_count}</FormFeedback>}
            <div className="form-text">Total attendees will be {totalAttendees}</div>
          </div>
          <div className="mb-3">
            <Label>Notes</Label>
            <Input name="notes" type="textarea" rows={3} value={formik.values.notes} onChange={formik.handleChange} />
          </div>
        </ModalBody>
        <ModalFooter>
          {onCancel && (
            <Button color="danger" type="button" onClick={onCancel} disabled={isSubmitting}>
              Cancel Registration
            </Button>
          )}
          <Button color="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save Registration'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default EventRegistrationModal;

